'use client';

import { useEffect } from 'react';
import { usePostHog } from 'posthog-js/react';
import { Button } from '@/components/ui/button';
import { markErrorAsCaptured } from '@/lib/error-tracking';

interface ErrorBoundaryProps {
  error: Error & { digest?: string };
  reset: () => void;
}

export default function ErrorBoundary({ error, reset }: ErrorBoundaryProps) {
  const posthog = usePostHog();

  useEffect(() => {
    // Log error to PostHog
    if (posthog) {
      posthog.capture('$exception', {
        message: error.message,
        name: error.name,
        stack: error.stack,
        digest: error.digest,
        type: 'unhandled_error',
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent,
        // Additional context
        errorCause: (error as any).cause,
        browserName:
          navigator.userAgent.match(
            /(firefox|chrome|safari|edge|opera)/i
          )?.[0] || 'unknown',
        viewportSize: `${window.innerWidth}x${window.innerHeight}`,
        screenSize: `${window.screen.width}x${window.screen.height}`,
        visibilityState: document.visibilityState
      });
      // Mark error as captured to prevent duplicate logging
      markErrorAsCaptured(error);
    }
  }, [error, posthog]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center">
      <div className="text-center">
        <h2 className="text-2xl font-semibold">Something went wrong!</h2>
        <p className="mt-2 text-gray-600">
          An error occurred while loading this page. We've been notified and are
          working on a fix.
        </p>
        <div className="mt-4 space-x-4">
          <Button onClick={reset}>Try again</Button>
          <Button
            variant="outline"
            onClick={() => (window.location.href = '/')}
          >
            Go home
          </Button>
        </div>
      </div>
    </div>
  );
}
